// NOTE! Keep header components and styles consistent between platform and docs
// Docs header styles: docs/src/styles/global.css
// Docs header nav: docs/public/scripts/header-nav.js

import {
  Box,
  Link as ChakraLink,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useBreakpointValue,
} from '@chakra-ui/react';
import styled from '@emotion/styled';
import LogoSource from '@resistapp/client/assets/Resistomap-new@3x.png';
import { useAnalytics } from '@resistapp/client/contexts/analytics-context';
import { useUrlContext } from '@resistapp/client/contexts/url-provider';
import {
  useAdminImpersonationStatus,
  useAdminStopImpersonation,
  useGetUser,
  useOrganisationLabWorkbooks,
  useProject,
} from '@resistapp/client/hooks/api';
import { useLabWorkbookLanding } from '@resistapp/client/hooks/use-lab-workbook-landing';
import { useNavigateWithQuery } from '@resistapp/client/hooks/use-navigate-with-query';
import {
  belongsOnlyToDemoOrganisations,
  belongsToLabOrganisation,
  belongsToNonDemoCustomerOrganisation,
  canAccessLabSystem,
  getAdminHomePath,
  isProviderAdminUser,
  isResistomapAdminUser,
  shouldForceLabLanding,
} from '@resistapp/client/utils/access';
import { markAuthChangingNavigation } from '@resistapp/client/utils/auth-changing-navigation';
import { shouldEnableProcessGroupLevel } from '@resistapp/client/utils/process-group-level';
import { getUserProjectIds } from '@resistapp/common/access';
import { hasMetricModeForProject, isAdmin } from '@resistapp/common/features';
import { LabWorkbookStatus, MetricMode, OrganisationType, User } from '@resistapp/common/types';
import { flattenSamplesByUID } from '@resistapp/common/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { NavLink, Link as RouterLink } from 'react-router-dom';
import { useUser } from '../../../contexts/use-user-context';
import { IconBase } from '../../icons/icon-base';
import { ProjectSelector } from '../../project-selector/project-selector'; // Import ProjectSelector
import { oldTheme } from '../../shared/old-styles';
import { theme } from '../../shared/theme';

type LinkType = {
  key: string;
  label: string;
  shortLabel?: string;
  to: string;
  disabled: boolean;
  disabledReason?: string;
  separator?: boolean;
};
type LinkArrays = LinkType[][];

export const headerHeight = 60;

interface Props {}
export function HeaderBar(_props: Props) {
  const { page, query, projectId } = useUrlContext();
  const navigate = useNavigateWithQuery();
  const queryClient = useQueryClient();
  const { user, logout, setLoggedIn } = useUser();
  const { resetAnalyticsState } = useAnalytics();
  const { send: fetchCurrentUser } = useGetUser();
  const stopImpersonationMutation = useAdminStopImpersonation();
  const impersonationStatusQuery = useAdminImpersonationStatus({ enabled: Boolean(user) });
  const shouldCheckProjectCapabilities =
    !!projectId.viable &&
    (page.current === 'index' ||
      page.current === 'risk' ||
      page.current === 'reduction' ||
      page.current === 'research');
  const { data: selectedProject } = useProject(projectId.viable ?? '', {
    enabled: shouldCheckProjectCapabilities,
  });
  const selectedProjectSamples = useMemo(
    () => (selectedProject ? flattenSamplesByUID(selectedProject.samplesByUID) : undefined),
    [selectedProject],
  );
  const reductionDisabledForProcessMode = useMemo(
    () => shouldEnableProcessGroupLevel(selectedProject?.processes, selectedProjectSamples),
    [selectedProject?.processes, selectedProjectSamples],
  );
  const forceLabLanding = shouldForceLabLanding(user);
  const { path: labLandingPath, organisationId: labWorkbookOrganisationId } = useLabWorkbookLanding(user, {
    enabled: forceLabLanding,
  });
  const labWorkbookOrganisation = useMemo(
    () => user?.organisations.find(organisation => organisation.id === labWorkbookOrganisationId) ?? null,
    [labWorkbookOrganisationId, user],
  );
  const shouldLoadCustomerLabWorkbooks =
    forceLabLanding && labWorkbookOrganisation?.type === OrganisationType.CUSTOMER && labWorkbookOrganisationId != null;
  const organisationLabWorkbooksQuery = useOrganisationLabWorkbooks(labWorkbookOrganisationId, {
    enabled: shouldLoadCustomerLabWorkbooks,
  });
  const customerWorkbookCount = shouldLoadCustomerLabWorkbooks
    ? organisationLabWorkbooksQuery.data?.workbooks.length ?? 0
    : 0;
  const hasMultipleCustomerLabWorkbooks = customerWorkbookCount > 1;
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const isMobileLg = useBreakpointValue({ base: true, '1700px': false });

  const activeStyle = ({ isActive }: { isActive: boolean }) =>
    isActive ? { color: theme.colors.blue900, backgroundColor: theme.colors.blue100 } : {};
  const isAdminUser = isAdmin(user);
  const belongsToLab = belongsToLabOrganisation(user);
  const belongsOnlyToDemo = belongsOnlyToDemoOrganisations(user);
  const canAccessLabDocs = canAccessLabSystem(user);
  const hasActiveCustomerLabWorkbook = Boolean(
    shouldLoadCustomerLabWorkbooks &&
      organisationLabWorkbooksQuery.data?.workbooks.some(
        workbook => workbook.status !== LabWorkbookStatus.COMPLETED && workbook.status !== LabWorkbookStatus.LEGACY,
      ),
  );
  const canAccessAdmin =
    !belongsOnlyToDemo &&
    (isAdminUser ||
      isResistomapAdminUser(user) ||
      isProviderAdminUser(user) ||
      belongsToLab ||
      belongsToNonDemoCustomerOrganisation(user) ||
      hasActiveCustomerLabWorkbook);
  const adminHomePath = getAdminHomePath(user);
  const adminLandingPath = forceLabLanding
    ? hasMultipleCustomerLabWorkbooks
      ? adminHomePath
      : labLandingPath ?? adminHomePath
    : adminHomePath;
  const projectLandingPath = projectId.viable != null ? `/${page.viable}/${projectId.viable}${query}` : '/world';
  const logoPath = !user
    ? '/world'
    : forceLabLanding
      ? adminLandingPath
      : canAccessAdmin
        ? adminHomePath
        : projectLandingPath;
  const activeImpersonationStatus =
    impersonationStatusQuery.isSuccess && impersonationStatusQuery.data.active ? impersonationStatusQuery.data : null;
  const impersonationTargetLabel = useMemo(() => {
    if (!activeImpersonationStatus) {
      return null;
    }

    if (activeImpersonationStatus.targetOrganisation?.isPseudoUser) {
      const role = activeImpersonationStatus.targetOrganisation.role;
      return `${activeImpersonationStatus.targetOrganisation.name}${role ? ` ${role.toLowerCase()}` : ''}`;
    }

    return activeImpersonationStatus.effectiveUser?.fullName ?? 'user';
  }, [activeImpersonationStatus]);

  const handleStopImpersonationStable = useCallback(async () => {
    const redirectPath =
      activeImpersonationStatus?.targetOrganisation?.id != null
        ? `/admin/organisation/${activeImpersonationStatus.targetOrganisation.id}`
        : '/admin/organisations';
    try {
      await stopImpersonationMutation.mutateAsync(undefined);
    } catch (error) {
      console.warn('Failed to stop impersonation', error);
      return;
    }

    resetAnalyticsState();

    await queryClient.cancelQueries();
    queryClient.clear();

    try {
      const response = await fetchCurrentUser();
      const isSuccessfulStatus =
        typeof response.status === 'number' && response.status >= 200 && response.status <= 204;
      if (!isSuccessfulStatus || !response.data) {
        throw new Error('Failed to refresh user after stopping impersonation.');
      }
      setLoggedIn(response.data as User);
      navigate(redirectPath, false);
    } catch (error) {
      console.warn('Failed to refresh user after stopping impersonation, forcing reload.', error);
      if (typeof window !== 'undefined') {
        markAuthChangingNavigation();
        window.location.assign(redirectPath);
      }
    }
  }, [
    activeImpersonationStatus?.targetOrganisation?.id,
    fetchCurrentUser,
    navigate,
    queryClient,
    resetAnalyticsState,
    setLoggedIn,
    stopImpersonationMutation,
  ]);

  const noProjectReason = 'Select a project';
  const subscriptionReason = 'Subscription required';
  const processModeReason = 'Not available in process mode';

  const link = {
    map: {
      key: 'map-link',
      label: 'Map',
      to: '/world',
      disabled: false,
    },
    resistanceIndex: {
      key: 'resistance-index-link',
      label: 'Resistance\u00A0Index',
      shortLabel: 'Index',
      to: `/index/${projectId.viable}${query}`,
      disabled: !projectId.viable || !hasMetricModeForProject(user, MetricMode.ARGI, projectId.viable),
      disabledReason: !projectId.viable ? noProjectReason : subscriptionReason,
    },
    healthRisk: {
      key: 'health-risk-link',
      label: 'Comparative\u00A0Risk',
      shortLabel: 'Risk',
      to: `/risk/${projectId.viable}${query}`,
      disabled: !projectId.viable || !hasMetricModeForProject(user, MetricMode.RISK, projectId.viable),
      disabledReason: !projectId.viable ? noProjectReason : subscriptionReason,
    },
    geneReduction: {
      key: 'gene-reduction-link',
      label: 'Gene\u00A0Reduction',
      shortLabel: 'Reduction',
      to: `/reduction/${projectId.viable}${query}`,
      disabled:
        !projectId.viable ||
        reductionDisabledForProcessMode ||
        !hasMetricModeForProject(user, MetricMode.REDUCTION, projectId.viable),
      disabledReason: !projectId.viable
        ? noProjectReason
        : reductionDisabledForProcessMode
          ? processModeReason
          : subscriptionReason,
    },
    analyse: {
      key: 'analyse-link',
      label: 'Gene\u00A0Details',
      shortLabel: 'Details',
      to: `/research/${projectId.viable}${query}`,
      disabled: !projectId.viable,
      disabledReason: noProjectReason,
      separator: true,
    },
  } as const satisfies Record<string, LinkType>;

  const adminMobileLinks: LinkArrays = [[link.resistanceIndex], []];
  const subscriberLinks: LinkArrays = [[link.resistanceIndex, link.healthRisk, link.geneReduction, link.analyse], []];
  const adminLinks: LinkArrays = [[...subscriberLinks[0]], []];
  // We don't show anything if user is not logged in or user uses small display
  const shownLinks: LinkArrays =
    canAccessAdmin && isMobile
      ? adminMobileLinks
      : canAccessAdmin
        ? adminLinks
        : !isMobile
          ? subscriberLinks
          : [[], []];
  const projectIds = getUserProjectIds(user);

  return (
    <Root id="header-bar" py={{ base: '20px', lg: '12px' }} px={6}>
      <Main>
        <Flex minWidth={{ base: 0, md: '100px', lg: '155px' }} width={{ base: 0, md: '155px' }}>
          <LogoContainer>
            <ChakraLink as={RouterLink} to={logoPath} display={{ base: 'none', md: 'block' }}>
              <Logo
                src={LogoSource as string}
                objectFit="contain"
                height={{ base: '0px', md: '60px' }}
                alt="Resistomap"
              />
            </ChakraLink>
            {activeImpersonationStatus ? (
              <ImpersonationBadge>
                <Box as="span" fontWeight={600}>
                  Impersonating {impersonationTargetLabel}
                </Box>
                <ChakraLink
                  as="button"
                  type="button"
                  onClick={() => {
                    void handleStopImpersonationStable();
                  }}
                  disabled={stopImpersonationMutation.isPending}
                  style={{ textDecoration: 'underline', fontWeight: 700 }}
                >
                  Stop
                </ChakraLink>
              </ImpersonationBadge>
            ) : null}
          </LogoContainer>
        </Flex>
        {page.current !== 'world' && user && projectIds.length > 0 && (
          <ProjectSelectorContainer>
            <ProjectSelector showStatus={isAdmin(user)} menuHeight="640px" />
          </ProjectSelectorContainer>
        )}
        <LinksContainer
          gap={{ base: 0, lg: theme.spacing[6], xl: theme.spacing[12] }}
          pl={{ base: theme.spacing[2], md: theme.spacing[12] }}
          pr={theme.spacing[4]}
          flexWrap={{ base: 'wrap', md: 'nowrap' }}
        >
          <Flex flexGrow={1} justifyContent="center">
            {shownLinks[0].map(linkLocal => (
              <LinkContainer key={linkLocal.key}>
                <Popover trigger={linkLocal.disabled ? 'hover' : undefined} placement="bottom" key={linkLocal.key}>
                  <PopoverTrigger>
                    <Box>
                      <HeaderLink
                        key={linkLocal.key}
                        to={linkLocal.to}
                        style={activeStyle}
                        disabled={linkLocal.disabled ? true : false}
                        separator={linkLocal.separator}
                      >
                        {isMobileLg && linkLocal.shortLabel ? linkLocal.shortLabel : linkLocal.label}
                      </HeaderLink>
                    </Box>
                  </PopoverTrigger>
                  <PopoverContent width="100%">
                    <PopoverBody>{linkLocal.disabledReason ?? 'Not available'}</PopoverBody>
                  </PopoverContent>
                </Popover>
              </LinkContainer>
            ))}
          </Flex>
          <Flex flexGrow={0}>
            {shownLinks[1].map(linkLocal => (
              <LinkContainer key={linkLocal.key} style={{ cursor: linkLocal.disabled ? 'not-allowed' : 'pointer' }}>
                <HeaderLink
                  key={linkLocal.key}
                  to={linkLocal.disabled ? '#' : linkLocal.to}
                  style={activeStyle}
                  disabled={linkLocal.disabled ? true : false}
                >
                  {isMobileLg && linkLocal.shortLabel ? linkLocal.shortLabel : linkLocal.label}
                </HeaderLink>
              </LinkContainer>
            ))}
          </Flex>
        </LinksContainer>
        <AccountContainer pr={theme.spacing[1]} justifyContent={{ base: 'center', lg: 'flex-end' }}>
          {user ? (
            <Menu placement="bottom-end">
              <MenuButton as="button" type="button" sx={accountMenuButtonStyle}>
                Platform ▾
              </MenuButton>
              <MenuList minW="180px" fontSize={theme.oldFontSize.xs}>
                {canAccessAdmin ? (
                  <MenuItem as={RouterLink} to={adminLandingPath}>
                    Admin
                  </MenuItem>
                ) : null}
                {canAccessLabDocs ? (
                  <MenuItem as="a" href="/docs/">
                    Laboratory System
                  </MenuItem>
                ) : null}
                <MenuItem
                  onClick={() => {
                    logout('/login');
                  }}
                >
                  Log out
                </MenuItem>
              </MenuList>
            </Menu>
          ) : (
            <HeaderLink to="/login" style={activeStyle}>
              Login
            </HeaderLink>
          )}
        </AccountContainer>
      </Main>
    </Root>
  );
}

const Root = styled(Box)`
  /* For some reason this does not work I expected on firefox, if we have border-box */
  box-sizing: content-box;
  z-index: ${theme.zIndexes.header};
  position: relative;
  background-color: ${theme.colors.neutral50};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 200%; /* 28px */
`;

const Main = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${theme.colors.neutral50};

  @media (max-width: 767px) {
    flex-wrap: wrap;
    row-gap: ${theme.spacing[3]};
  }
`;

const LogoContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const ImpersonationBadge = styled.div`
  position: absolute;
  top: -6px;
  left: 6px;
  z-index: ${theme.zIndexes.header + 1};
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  background-color: ${theme.colors.red100};
  color: ${theme.colors.neutral900};
  border: 1px solid ${theme.colors.red300};
  border-radius: 999px;
  padding: 2px 10px;
  font-size: ${theme.oldFontSize.xs};
  line-height: 1.4;
  white-space: nowrap;
`;

export const ProjectSelectorContainer = styled(Flex)`
  position: relative;
  width: 100%;
  margin-left: ${theme.spacing[3]};

  @media (max-width: 767px) {
    order: 4;
    flex-basis: 100%;
    margin-left: 0;
  }
`;

const LinksContainer = styled(Flex)`
  justify-content: center;
  align-items: center;
  width: 100%;

  @media (max-width: 767px) {
    flex: 1;
    width: auto;
  }
`;

const AccountContainer = styled(Flex)`
  white-space: nowrap;
  align-items: center;
  padding-top: ${oldTheme.spacing.xxs};
  padding-bottom: ${oldTheme.spacing.xxs};
`;

const Logo = styled(Image)`
  box-sizing: border-box;
  padding-right: 0px;
  padding-left: 5px;
  padding-top: 13.5px;
  padding-bottom: 13.5px;
  margin: 0;
  background-color: ${theme.colors.neutral50};
`;

const iconStyle = `
   margin-left: 6px;
   margin-bottom: -0.3px;
`;

export const HomeIcon = styled(IconBase)`
  ${iconStyle}
`;

const LinkContainer = styled(Flex)`
  position: relative;
  display: flex;
  align-items: center;
`;

const headerLinkStyle = {
  '&:hover': {
    textDecoration: 'none',
    color: theme.colors.neutral700,
  },
  display: 'flex',
  color: theme.colors.neutral700,
  fontSize: theme.oldFontSize.xs,
  fontStyle: 'normal',
  fontWeight: 501,
  lineHeight: '28px',
  padding: '12px 18px',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '10px',
  borderRadius: '9999px',
};

const accountMenuButtonStyle = {
  ...headerLinkStyle,
  border: 'none',
  backgroundColor: 'transparent',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'none',
    color: theme.colors.neutral700,
    backgroundColor: theme.colors.blue100,
  },
};

function HeaderLink({
  disabled = false,
  separator = false,
  style,
  children,
  to,
  onClick,
}: {
  disabled?: boolean;
  separator?: boolean;
  style?: ({ isActive }: { isActive: boolean }) => React.CSSProperties;
  children: React.ReactNode;
  to: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}) {
  return (
    <Flex alignItems="center">
      {separator && (
        <div
          style={{
            width: '1px',
            height: '24px',
            backgroundColor: theme.colors.neutral300,
            marginLeft: theme.spacing[2],
            marginRight: theme.spacing[2],
          }}
        />
      )}
      <div>
        <NavLink
          style={({ isActive }) => ({
            ...style,
            ...headerLinkStyle,
            opacity: disabled ? 0.5 : 1,
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: isActive ? theme.colors.blue100 : 'transparent',
          })}
          to={to}
          onClick={e => {
            if (disabled) {
              e.preventDefault();
            } else {
              onClick?.(e);
            }
          }}
        >
          {children}
        </NavLink>
      </div>
    </Flex>
  );
}
